"use client";

import Image from "next/image";
import React, { useEffect, useMemo, useRef } from "react";
import { DEFAULT_TECH_STACK } from "@/data/tech-stack";

export interface TechStackItem {
  id: string;
  name: string;
  iconUrl: string;
}

interface TechStackTickerProps {
  items?: TechStackItem[];
}

const TechStackTicker = ({ items }: TechStackTickerProps) => {
  // Combine database items with rich default curated stack (deduplicating by name)
  const allItems = useMemo(() => {
    const combined = [...(items || []),...DEFAULT_TECH_STACK];
    const seen = new Set<string>();
    return combined.filter((item) => {
      const key = item.name.toLowerCase().trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [items]);

  if (allItems.length === 0) return null;

  // Split into two balanced rows for multi-directional scrolling
  const mid = Math.ceil(allItems.length / 2);
  const row1 = allItems.slice(0, mid);
  const row2 = allItems.slice(mid);

  return (
    <section className="relative z-10 py-16 bg-transparent border-t border-white/5 overflow-hidden space-y-8">
      <div className="container mx-auto px-4 md:px-6 text-center">
        <p className="text-sm font-semibold tracking-widest text-muted-foreground uppercase">
          Powering Your Solutions With
        </p>
      </div>

      <div className="flex flex-col gap-6 relative">
        {/* Gradient Masks */}
        <div className="absolute left-0 top-0 z-20 h-full w-20 md:w-40 bg-gradient-to-r from-background to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 z-20 h-full w-20 md:w-40 bg-gradient-to-l from-background to-transparent pointer-events-none" />

        {/* First Line - Scroll Left (AI & Automation & Frontend) */}
        <TickerLine items={row1} direction="left" speed={50} />

        {/* Second Line - Scroll Right (Backend & Cloud & Compliance) */}
        <TickerLine items={row2} direction="right" speed={55} />
      </div>
    </section>
  );
};

const TickerLine = ({
  items,
  direction,
  speed,
}: {
  items: TechStackItem[];
  direction: "left" | "right";
  speed: number;
}) => {
  // Double the items for smooth looping
  const rowItems = [...items, ...items];

  // Mobile has no hover, so instead the logo sitting at the horizontal
  // center of the ticker is highlighted as it scrolls past. Desktop keeps
  // the plain group-hover behavior below untouched.
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    let isMobile = mql.matches;
    let activeIndex: number | null = null;
    let rafId: number;

    const setActive = (el: HTMLDivElement | null, on: boolean) => {
      if (!el) return;
      el.style.filter = on ? "grayscale(0)" : "";
      el.style.opacity = on ? "1" : "";
      el.style.transform = on ? "scale(1.1)" : "";
    };

    const clearActive = () => {
      if (activeIndex !== null) {
        setActive(itemRefs.current[activeIndex], false);
        activeIndex = null;
      }
    };

    const tick = () => {
      if (isMobile && containerRef.current) {
        const centerX =
          containerRef.current.getBoundingClientRect().left +
          containerRef.current.getBoundingClientRect().width / 2;

        let nextIndex: number | null = null;
        for (let i = 0; i < itemRefs.current.length; i++) {
          const el = itemRefs.current[i];
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (centerX >= rect.left && centerX <= rect.right) {
            nextIndex = i;
            break;
          }
        }

        if (nextIndex !== activeIndex) {
          setActive(itemRefs.current[activeIndex ?? -1], false);
          setActive(itemRefs.current[nextIndex ?? -1], true);
          activeIndex = nextIndex;
        }
      }
      if (running) rafId = requestAnimationFrame(tick);
    };

    const handleChange = (e: MediaQueryListEvent) => {
      isMobile = e.matches;
      if (!isMobile) clearActive();
    };

    mql.addEventListener("change", handleChange);

    // Only run the per-frame highlight loop while the ticker is on screen:
    // it reads ~100 bounding rects every frame, which forced a layout per
    // frame for the whole page lifetime (SEO spec 3.4).
    let running = false;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !running) {
        running = true;
        rafId = requestAnimationFrame(tick);
      } else if (!entry.isIntersecting && running) {
        running = false;
        cancelAnimationFrame(rafId);
        clearActive();
      }
    });
    if (containerRef.current) io.observe(containerRef.current);

    return () => {
      io.disconnect();
      mql.removeEventListener("change", handleChange);
      cancelAnimationFrame(rafId);
      clearActive();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex overflow-hidden select-none py-6 group/ticker"
    >
      <div
        className="flex flex-shrink-0 items-center hover:[animation-play-state:paused] group-hover/ticker:[animation-play-state:paused]"
        style={{
          animation: `scrollX ${speed}s linear infinite ${direction === "right" ? "reverse" : "normal"}`,
        }}
      >
        {rowItems.map((item, idx) => (
          <div
            key={`${item.id}-${idx}`}
            // Fixed width container for equal spacing relative to centers
            className="w-36 sm:w-44 md:w-52 flex flex-col justify-center items-center group/item px-3 relative"
            title={item.name}
          >
            {/* Tooltip Title on Hover */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 -translate-y-1 opacity-0 group-hover/item:opacity-100 group-hover/item:translate-y-0 transition-all duration-200 pointer-events-none z-30 flex flex-col items-center">
              <span className="px-2.5 py-1 rounded-md bg-neutral-900/95 dark:bg-neutral-800/95 text-neutral-100 text-xs font-medium tracking-wide whitespace-nowrap shadow-lg shadow-black/30 border border-white/10 backdrop-blur-md">
                {item.name}
              </span>
              <span className="w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-neutral-900/95 dark:border-t-neutral-800/95" />
            </div>

            <div
              ref={(el) => {
                itemRefs.current[idx] = el;
              }}
              className="relative h-16 w-full flex items-center justify-center transition-all duration-300 group-hover/item:scale-110"
            >
              {/* next/image, not a raw <img>: these logos are admin-uploaded
                  (Cloudinary) or hosted on third-party sites (wikimedia, hl7)
                  at their full source resolution — a 1080x800 PNG for a 56px
                  slot, served with no cache headers at all. Routing them
                  through /_next/image resizes them to the slot, converts to
                  avif/webp, and puts them behind our own 1-year immutable
                  cache instead of the origin's. `sizes` is the CSS height's
                  worst case (h-14 = 56px, so 112px at 2x); the intrinsic
                  aspect ratio is unknown per logo, hence width/height are the
                  slot's box and object-contain letterboxes inside it. */}
              <Image
                src={item.iconUrl}
                alt={item.name}
                loading="lazy"
                decoding="async"
                width={112}
                height={56}
                sizes="112px"
                unoptimized={item.iconUrl.includes(".svg")}
                className="h-12 md:h-14 w-auto object-contain max-w-full drop-shadow-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TechStackTicker;
